<template>
  <ClientOnly>
    <div class="rebate-page">
      <RebateClaimBanner
        :history-total="rebateHistoryTotal"
        :amount="rebateClaimable"
        :disabled="!enabled"
        :settle="rebateSettle"
        :claimed="isClaimed"
        :loading="claiming"
        @claim="onClaim"
        @help="onHelp"
        @refresh="onTimeReached"
      />

      <div class="rebate-body">
        <aside class="categories">
          <button
            v-for="item in categories"
            :key="item.code"
            type="button"
            :class="{ active: category === item.code }"
            @click="category = item.code"
          >
            <span class="icon">{{ item.icon }}</span>
            <span>{{ item.label }}</span>
            <i v-if="item.claimableCount > 0" class="cat-badge">{{
              item.claimableCount > 99 ? '99+' : item.claimableCount
            }}</i>
          </button>
        </aside>
        <section class="game-list">
          <RebateGameRow
            v-for="row in visibleRows"
            :key="row.code"
            :row="row"
            @open="onOpenRow"
            @expire="onTimeReached"
          />
        </section>
      </div>

      <Transition name="toast-fade">
        <div v-if="toastMessage" class="rebate-toast">{{ toastMessage }}</div>
      </Transition>

      <RebateRulesDrawer
        v-model="rulesOpen"
        :reward-target="panel?.reward_target"
        :wagering-multiple="panel?.wagering_multiple"
        @close="rulesOpen = false"
      />

      <RebateRateDetailDrawer
        v-model="rateDetailOpen"
        :initial-game-type="category"
        @close="rateDetailOpen = false"
      />
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import { formatBaseAmt } from '~/utils/transfer/money'
import { claimRakeback, fetchRakebackPanel } from '~/api/promo'
import aeIcon from './img/ae.webp'
import jdbIcon from './img/jdb.webp'
import jiliIcon from './img/jili.webp'
import pgIcon from './img/pg.webp'
import ygIcon from './img/yg.webp'
import type {
  RakebackPoolGroupDto,
  RakebackSettleInfoDto,
  RakebackVendorPoolDto,
} from '~/types/api/promo'
import type { RebateGameRow as RebateGameRowType } from './types'
import RebateClaimBanner from './components/RebateClaimBanner.vue'
import RebateGameRow from './components/RebateGameRow.vue'
import RebateRateDetailDrawer from './components/RebateRateDetailDrawer.vue'
import RebateRulesDrawer from './components/RebateRulesDrawer.vue'

const userStore = useUserStore()
const { t, te } = useI18n()
const toNumber = (value: string) => Number(value) || 0

const gameTypeLabel = (code: string, fallback: string) =>
  te(`promo.rebate.gameType.${code}`) ? t(`promo.rebate.gameType.${code}`) : fallback

type CategoryItem = {
  code: string
  label: string
  icon: string
  claimableCount: number
}

const CATEGORY_ICONS: Record<string, string> = {
  original: '🎲',
  slots: '🎰',
  card: '♠️',
  live: '🃏',
  lottery: '🎫',
  fish: '🐟',
  sport: '⚽',
  esport: '🎮',
}

const VENDOR_ICONS: Record<string, string> = {
  Pg: pgIcon,
  Jdbdz: jdbIcon,
  Jili: jiliIcon,
  Ag: aeIcon,
  Ky: ygIcon,
}

const resolveIcon = (v: RakebackVendorPoolDto) =>
  v.icon || v.vendor_icon || VENDOR_ICONS[v.vendor_code] || pgIcon

const mapCategory = (g: RakebackPoolGroupDto): CategoryItem => ({
  code: g.game_type,
  label: gameTypeLabel(g.game_type, g.game_type_name),
  icon: CATEGORY_ICONS[g.game_type] ?? '🎮',
  claimableCount: Math.max(0, g.claimable_count ?? 0),
})

const mapRow = (g: RakebackPoolGroupDto) =>
  g.vendors.map((v): RebateGameRowType => ({
    code: v.vendor_code,
    name: v.vendor_name,
    icon: resolveIcon(v),
    betAmount: toNumber(v.valid_turnover),
    rate: toNumber(v.current_rate) * 100,
    rebate: toNumber(v.accrued),
    expireAt: v.expire_at,
  }))

const { data: panel, refresh } = useAsyncData('rewards-rakeback', () => fetchRakebackPanel())

const claimedCategories = ref<Set<string>>(new Set())
const categories = ref<CategoryItem[]>([])
const rowsMap = ref<Record<string, RebateGameRowType[]>>({})
const category = ref<string>('')

watch(
  () => panel.value,
  val => {
    if (!val) return
    const mapped = val.groups.map(g => ({ category: mapCategory(g), rows: mapRow(g) }))
    categories.value = mapped.map(m => m.category)
    const next: Record<string, RebateGameRowType[]> = {}
    mapped.forEach(m => {
      next[m.category.code] = m.rows
    })
    rowsMap.value = next
    if (!categories.value.some(c => c.code === category.value) && categories.value.length > 0) {
      category.value = categories.value[0]!.code
    }
  },
  { immediate: true },
)

const visibleRows = computed(() => rowsMap.value[category.value] ?? [])

const selectedGroup = computed(() => panel.value?.groups.find(g => g.game_type === category.value))
const claimScope = computed(() => panel.value?.claim_granularity ?? 'all')

const rebateClaimable = computed(() => {
  if (!panel.value?.enabled) return 0
  if (claimScope.value === 'all') {
    return Math.max(0, toNumber(panel.value.claimable_total))
  }
  return Math.max(0, toNumber(selectedGroup.value?.subtotal ?? '0'))
})

const enabled = computed(() => {
  if (!panel.value?.enabled) return false
  if (claimScope.value === 'all') {
    return panel.value.can_claim
  }
  return selectedGroup.value?.can_claim
})

const rebateHistoryTotal = computed(() => toNumber(panel.value?.total_claimed ?? '0'))
/** 派发状态：领取后以领取结果的 settle 为准，刷新面板后以面板为准 */
const claimSettle = ref<RakebackSettleInfoDto | null>(null)
const rebateSettle = computed(() => claimSettle.value ?? panel.value?.settle ?? null)

/** 派发时刻 / 返水池过期时刻到点：重新拉面板，不在本地改状态 */
const onTimeReached = () => {
  claimSettle.value = null
  void refresh()
}

const isClaimed = computed(() => {
  if (claimScope.value === 'all') {
    return (
      categories.value.length > 0 &&
      categories.value.every(c => claimedCategories.value.has(c.code))
    )
  }
  return claimedCategories.value.has(category.value)
})

const rateDetailOpen = ref(false)
const onOpenRow = (_row: RebateGameRowType) => {
  rateDetailOpen.value = true
}

const { load: loadOverview } = useRewardOverview()
const toastMessage = ref('')
let toastTimer: ReturnType<typeof setTimeout> | null = null
const showToast = (message: string) => {
  if (toastTimer) clearTimeout(toastTimer)
  toastMessage.value = message
  toastTimer = setTimeout(() => {
    toastMessage.value = ''
  }, 2000)
}

const claiming = ref(false)

const onClaim = async () => {
  if (rebateClaimable.value <= 0 || claiming.value) return
  claiming.value = true
  const scope = claimScope.value
  try {
    const result = await claimRakeback(
      scope === 'perType' ? { scope, game_type: category.value } : { scope },
    )
    void loadOverview() // 领取后刷新页签角标
    if (scope === 'all') {
      categories.value.forEach(c => claimedCategories.value.add(c.code))
    } else {
      claimedCategories.value.add(category.value)
    }
    const amount = toNumber(result.amount)
    const message =
      result.status === 'SUCCEEDED'
        ? t('promo.rebate.claimSucceeded', {
            amount: formatBaseAmt(amount, userStore.baseCurrency),
          })
        : t('promo.rebate.claimPending', { amount: formatBaseAmt(amount, userStore.baseCurrency) })
    showToast(message)
    claimSettle.value = result.settle ?? null
    await refresh()
    claimSettle.value = null
  } catch (error) {
    console.error('Claim rakeback failed:', error)
    showToast(t('promo.rebate.claimFailed'))
  } finally {
    claiming.value = false
  }
}

const rulesOpen = ref(false)
const onHelp = () => {
  rulesOpen.value = true
}
</script>

<style scoped lang="scss">
.rebate-page {
  display: block;
  animation: fade 0.2s ease;
}

.rebate-body {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 14px 0;
}

.sidebar-wrap {
  flex: 0 0 60px;
  position: sticky;
  top: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.categories {
  flex: 0 0 60px;
  position: sticky;
  top: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.categories button {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 10px 4px;
  overflow: visible;
  border-radius: 14px;
  background: var(--surface);
  border: 1px solid var(--hairline);
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
}

.categories button.active {
  background: linear-gradient(
    135deg,
    rgba(var(--primary-rgb), 0.28),
    rgba(var(--primary2-rgb), 0.16)
  );
  border-color: var(--primary);
  color: #fff;
}

.categories .icon {
  font-size: 21px;
  font-style: normal;
}

.cat-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--danger);
  color: #fff;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
  line-height: 1;
  pointer-events: none;
}

.game-list {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 10px;
}

.rebate-toast {
  position: fixed;
  left: 50%;
  bottom: calc(var(--tabbar-h) + 24px + env(safe-area-inset-bottom, 0px));
  transform: translateX(-50%);
  z-index: 80;
  padding: 10px 18px;
  border-radius: var(--r-pill);
  background: rgba(20, 18, 30, 0.92);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  box-shadow: var(--shadow);
  white-space: nowrap;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(6px);
}
</style>
